import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiAlertTriangle, FiCheckCircle, FiSend, FiShoppingCart, FiX } from 'react-icons/fi';
import CartPanel from './CartPanel';
import CategoryFilterBar from './CategoryFilterBar';
import Modal from './Modal';
import PosProductCard from './PosProductCard';
import ProductGrid from './ProductGrid';
import VarietyDetailModal from './VarietyDetailModal';
import VarietyGroupTabs from './VarietyGroupTabs';
import {
  isResolvedProductSoldOut,
  isResolvedVarietySoldOut,
} from '../utils/productAvailability';
import { normalizeQuantity } from '../utils/quantity';
import { ALL_VARIETY_GROUP_ID, filterVarietiesByGroup } from '../utils/varietyGroups';

const formatQuantity = (value) => {
  const quantity = normalizeQuantity(value);
  const hasDecimals = Math.abs(quantity - Math.round(quantity)) > 0.0001;
  return quantity.toLocaleString('de-DE', hasDecimals
    ? { minimumFractionDigits: 0, maximumFractionDigits: 3 }
    : undefined);
};

const buildFormatLabel = (format) =>
  [format?.metricUnit, format?.description]
    .map((entry) => String(entry || '').trim())
    .filter(Boolean)
    .join(' · ') || 'Formato';

const normalizeCategoryKey = (product) => {
  const raw = product?.categoryId ?? product?.Category?.id ?? 'uncategorized';
  const normalized = String(raw || '').trim();
  return normalized || 'uncategorized';
};

const resolveCategoryLabel = (product) => {
  const category = String(product?.Category?.name || '').trim();
  if (category) return category;
  if (product?.categoryId) return `Categoria ${product.categoryId}`;
  return 'Sin categoria';
};

const withProductContext = (product, variety) => ({
  ...variety,
  productName: product?.productName,
  productImage: product?.productImage,
  Product: product,
});

const buildInitialCart = (items = []) => (
  (Array.isArray(items) ? items : []).reduce((acc, item) => {
    const varietyPriceId = item?.varietyPriceId;
    const quantity = normalizeQuantity(item?.quantity ?? item?.requestedQuantity ?? item?.currentQuantity ?? 0);
    if (!varietyPriceId || quantity <= 0) return acc;
    const price = Number(item?.price || 0);
    const discount = Math.min(Math.max(Number(item?.discount || 0), 0), Math.max(price, 0));
    acc[String(varietyPriceId)] = {
      varietyId: item.varietyId,
      varietyPriceId,
      priceIndex: Number.isInteger(Number(item.priceIndex)) ? Number(item.priceIndex) : 0,
      productName: item.productName,
      productImage: item.productImage,
      varietyName: item.varietyName,
      varietyImage: item.varietyImage,
      priceImage: item.priceImage,
      metricUnit: item.metricUnit,
      description: item.priceDescription || item.description || item.metricUnit,
      price,
      discount,
      discountSource: discount > 0 ? (item.discountSource || 'client_category') : 'none',
      finalPrice: Math.max(price - discount, 0),
      subtotal: Math.floor(Math.max(price - discount, 0) * quantity),
      quantity,
    };
    return acc;
  }, {})
);

const PurchaseOrderCatalogEditor = ({
  catalog = [],
  initialItems = [],
  initialNotes = '',
  resetKey = '',
  submitLabel = 'Guardar orden',
  cartTitle = 'Orden de compra',
  cartDescription = 'Los totales se muestran antes de guardar.',
  notesPlaceholder = 'Notas para la orden (opcional)',
  onSubmit,
  topContent = null,
  successContent = null,
  showNotes = true,
  layout = 'page',
  productModalZClassName = 'z-30',
  varietyModalZClassName = 'z-50',
  quantityModalZClassName = 'z-50',
  className = '',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [modalProduct, setModalProduct] = useState(null);
  const [modalVariety, setModalVariety] = useState(null);
  const [modalFormat, setModalFormat] = useState(null);
  const [activeVarietyGroupId, setActiveVarietyGroupId] = useState(ALL_VARIETY_GROUP_ID);
  const [showMobileCart, setShowMobileCart] = useState(false);
  const [cart, setCart] = useState(() => buildInitialCart(initialItems));
  const [notes, setNotes] = useState(initialNotes || '');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setCart(buildInitialCart(initialItems));
    setNotes(initialNotes || '');
    setError('');
  }, [resetKey]);

  useEffect(() => {
    setActiveVarietyGroupId(ALL_VARIETY_GROUP_ID);
  }, [modalProduct?.id]);

  const categoryOptions = useMemo(() => {
    const map = new Map();
    catalog.forEach((product) => {
      const id = normalizeCategoryKey(product);
      if (!map.has(id)) {
        const label = resolveCategoryLabel(product);
        map.set(id, {
          id,
          label,
          initial: label.charAt(0).toUpperCase() || '?',
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [catalog]);

  const productMatchesSearch = (product, search) => {
    if (!search) return true;
    const productName = String(product.productName || '').toLowerCase();
    const varietyText = (product.Varieties || [])
      .map((variety) => [
        variety.varietyName,
        variety.description,
        ...(variety.VarietyPrices || []).map((format) => buildFormatLabel(format)),
      ].join(' '))
      .join(' ')
      .toLowerCase();
    return productName.includes(search) || varietyText.includes(search);
  };

  const filteredProducts = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    return catalog.filter((product) => {
      const categoryId = normalizeCategoryKey(product);
      if (selectedCategory !== 'all' && categoryId !== String(selectedCategory)) return false;
      return productMatchesSearch(product, search);
    });
  }, [catalog, searchTerm, selectedCategory]);

  const categoriesToRender = useMemo(() => {
    if (searchTerm.trim() || selectedCategory === 'all') return categoryOptions;
    return categoryOptions.filter((category) => String(category.id) === String(selectedCategory));
  }, [categoryOptions, searchTerm, selectedCategory]);

  const cartItems = useMemo(
    () => Object.values(cart).filter((item) => normalizeQuantity(item.quantity) > 0),
    [cart]
  );

  const cartCount = cartItems.reduce((sum, item) => sum + normalizeQuantity(item.quantity), 0);
  const cartTotal = cartItems.reduce(
    (sum, item) => sum + Math.floor(Math.max(Number(item.price || 0) - Number(item.discount || 0), 0) * normalizeQuantity(item.quantity)),
    0
  );

  const quantities = useMemo(() => {
    const next = {};
    cartItems.forEach((item) => {
      if (!Number.isInteger(Number(item.varietyId)) || !Number.isInteger(Number(item.priceIndex))) return;
      if (!next[item.varietyId]) next[item.varietyId] = [];
      next[item.varietyId][item.priceIndex] = item.quantity;
    });
    return next;
  }, [cartItems]);

  const findSelection = useCallback((varietyId, priceIndex, varietyPriceId = null) => {
    for (const product of catalog) {
      const variety = (product.Varieties || []).find(
        (entry) => Number(entry.id) === Number(varietyId)
      );
      if (!variety) continue;
      const formats = variety.VarietyPrices || [];
      let resolvedIndex = Number.isInteger(Number(priceIndex)) ? Number(priceIndex) : -1;
      if (varietyPriceId != null) {
        const byId = formats.findIndex((format) => Number(format.id) === Number(varietyPriceId));
        if (byId >= 0) resolvedIndex = byId;
      }
      const format = formats[resolvedIndex];
      if (!format) return null;
      return {
        product,
        variety: withProductContext(product, variety),
        format,
        priceIndex: resolvedIndex,
      };
    }
    return null;
  }, [catalog]);

  const findVarietyForItem = useCallback((item) => (
    findSelection(item?.varietyId, item?.priceIndex, item?.varietyPriceId)?.variety || null
  ), [findSelection]);

  const upsertCartItem = ({ product, variety, format, priceIndex, quantity }) => {
    if (!format?.id || format.isSoldOut) return;
    const nextQuantity = normalizeQuantity(quantity);
    setError('');
    setCart((prev) => {
      const key = String(format.id);
      if (nextQuantity <= 0) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return {
        ...prev,
        [key]: {
          varietyId: variety.id,
          varietyPriceId: format.id,
          priceIndex,
          productName: product.productName,
          productImage: product.productImage,
          varietyName: variety.varietyName,
          varietyImage: variety.varietyImage,
          priceImage: format.priceImage,
          metricUnit: format.metricUnit,
          description: buildFormatLabel(format),
          price: Number(format.price || 0),
          discount: Math.min(Math.max(Number(format.discount || 0), 0), Math.max(Number(format.price || 0), 0)),
          discountSource: Number(format.discount || 0) > 0 ? (format.discountSource || 'client_category') : 'none',
          finalPrice: Number.isFinite(Number(format.finalPrice))
            ? Number(format.finalPrice)
            : Math.max(Number(format.price || 0) - Number(format.discount || 0), 0),
          subtotal: Math.floor(Math.max(Number(format.price || 0) - Number(format.discount || 0), 0) * nextQuantity),
          quantity: nextQuantity,
        },
      };
    });
  };

  const handleQuantityChange = (varietyId, priceIndex, value) => {
    const selection = findSelection(varietyId, priceIndex);
    if (!selection) return;
    upsertCartItem({ ...selection, quantity: value });
  };

  const handleQuantityIncrement = (varietyId, priceIndex, delta) => {
    const selection = findSelection(varietyId, priceIndex);
    if (!selection || selection.format?.isSoldOut) return;
    const current = normalizeQuantity(cart[String(selection.format.id)]?.quantity || 0);
    upsertCartItem({ ...selection, quantity: Math.max(current + delta, 0) });
  };

  const handleRemoveItem = (item) => {
    setError('');
    setCart((prev) => {
      const next = { ...prev };
      delete next[String(item.varietyPriceId)];
      return next;
    });
  };

  const resetEditor = () => {
    setCart({});
    setNotes('');
    setModalProduct(null);
    setModalVariety(null);
    setModalFormat(null);
    setShowMobileCart(false);
    setError('');
  };

  const handleSubmit = async () => {
    setError('');
    if (!cartItems.length) {
      setError('Agrega al menos un producto.');
      return;
    }
    setSubmitLoading(true);
    try {
      const payloadItems = cartItems.map((item) => ({
        varietyPriceId: item.varietyPriceId,
        quantity: normalizeQuantity(item.quantity),
      }));
      await onSubmit?.({
        items: payloadItems,
        notes: showNotes ? (notes.trim() || null) : null,
        cartItems,
        resetEditor,
      });
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'No se pudo guardar la orden.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const isVarietySoldOut = isResolvedVarietySoldOut;
  const isProductSoldOut = isResolvedProductSoldOut;

  const openVariety = (product, variety) => {
    if (isVarietySoldOut(variety)) return;
    setModalVariety(withProductContext(product, variety));
  };

  const handlePriceCardIncrement = (_format, priceIndex) => {
    if (!modalVariety) return;
    handleQuantityIncrement(modalVariety.id, priceIndex, 1);
  };

  const handlePriceCardDecrement = (_format, priceIndex) => {
    if (!modalVariety) return;
    handleQuantityIncrement(modalVariety.id, priceIndex, -1);
  };

  const renderCategoryPicker = (itemsWrapperClassName = '') => (
    <CategoryFilterBar
      categories={categoryOptions}
      selectedCategory={selectedCategory}
      onSelect={setSelectedCategory}
      includeAll
      allItem={{ id: 'all', label: 'Todos', initial: 'T' }}
      emptyMessage="No hay categorias disponibles."
      isEmpty={categoryOptions.length === 0}
      itemsWrapperClassName={itemsWrapperClassName}
    />
  );

  const renderCartHeader = () => (
    <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2">
      <div className="text-sm font-semibold text-gray-900">{cartTitle}</div>
      <div className="mt-0.5 text-xs text-gray-600">{cartDescription}</div>
    </div>
  );

  const renderCartFooter = () => (
    <>
      {showNotes && (
        <textarea
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder={notesPlaceholder}
          rows={3}
          className="mt-3 w-full resize-none rounded-lg border border-gray-300 p-3"
        />
      )}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={submitLoading || !cartItems.length}
        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-3 text-white hover:bg-blue-700 disabled:opacity-60"
      >
        <FiSend />
        {submitLoading ? 'Guardando...' : submitLabel}
      </button>
    </>
  );

  const renderCartPanel = ({ mobile = false, modal = false } = {}) => (
    <CartPanel
      items={cartItems}
      getVariety={findVarietyForItem}
      getItemQuantity={(item) => item.quantity}
      onItemClick={(item, variety) => {
        if (!variety) return;
        setModalVariety(variety);
      }}
      onRemoveItem={handleRemoveItem}
      canEdit
      headerContent={renderCartHeader()}
      panelClassName={mobile
        ? 'max-h-[55vh] overflow-y-auto bg-white'
        : modal
          ? 'min-h-0 flex-1 overflow-y-auto rounded-t-xl border border-b-0 border-gray-200 bg-white p-4'
          : 'rounded-lg bg-white p-4 shadow-lg md:h-[calc(86vh-210px)] md:max-h-[calc(86vh-210px)] md:overflow-y-auto'}
      footerClassName={modal
        ? 'sticky bottom-0 z-20 mt-0 flex shrink-0 flex-col gap-1 rounded-b-xl border border-gray-200 bg-white px-4 pb-4 pt-3 shadow-[0_-8px_20px_rgba(15,23,42,0.08)]'
        : 'mt-4 flex flex-col gap-1 pb-1'}
      itemsSubtotal={cartTotal}
      totalAmount={cartTotal}
      showPrices
      showSummaryBreakdown
      showTotal
      footerActions={renderCartFooter()}
    />
  );

  const renderFeedbackNotice = () => {
    const text = error;
    if (!text && !successContent) return null;
    if (successContent && !text) {
      return (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <FiCheckCircle size={18} />
            </span>
            <div className="min-w-0 flex-1">{successContent}</div>
          </div>
        </div>
      );
    }
    return (
      <div className="mb-4 rounded-xl border border-red-200 bg-white px-4 py-3 shadow-sm" role="alert">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-red-100 text-red-700">
            <FiAlertTriangle size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900">No se pudo guardar</p>
            <p className="mt-0.5 text-sm text-red-700">{text}</p>
          </div>
          <button
            type="button"
            onClick={() => setError('')}
            className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Cerrar aviso"
          >
            <FiX size={16} />
          </button>
        </div>
      </div>
    );
  };

  const renderProductCard = (product) => {
    const imageSrc = product.productImage || 'https://placehold.co/200';
    const isSoldOut = isProductSoldOut(product);
    return (
      <PosProductCard
        key={product.id}
        productName={product.productName}
        imageSrc={imageSrc}
        badge={isSoldOut ? 'Agotado' : ''}
        onClick={() => setModalProduct(product)}
      />
    );
  };

  const renderProductGrid = () => (
    <>
      <div className="mb-4 flex items-center justify-between text-sm text-gray-600">
        <div>Mostrando {filteredProducts.length} resultados</div>
        <div className="font-semibold text-blue-700 md:hidden">
          {formatQuantity(cartCount)} en carrito
        </div>
      </div>
      <div className="space-y-8">
        {categoriesToRender.map((category) => {
          const productsInCategory = filteredProducts
            .filter((product) => normalizeCategoryKey(product) === String(category.id))
            .sort((a, b) => String(a.productName || '').localeCompare(String(b.productName || '')));
          if (productsInCategory.length === 0) return null;
          return (
            <div key={category.id}>
              <div className="my-2 flex items-center gap-4">
                <div className="flex-grow border-t border-gray-300"></div>
                <h2 className="whitespace-nowrap text-lg font-bold text-gray-700">
                  {category.label || 'Categoria'}
                </h2>
                <div className="flex-grow border-t border-gray-300"></div>
              </div>
              <ProductGrid>
                {productsInCategory.map(renderProductCard)}
              </ProductGrid>
            </div>
          );
        })}
      </div>
      {!filteredProducts.length && (
        <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-gray-600">
          No hay productos disponibles.
        </div>
      )}
    </>
  );

  const renderProductModal = () => {
    if (!modalProduct) return null;
    return (
      <div
        className={`fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 ${productModalZClassName} px-4 py-6`}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className="relative flex max-h-[86vh] w-full max-w-3xl flex-col rounded-lg bg-white p-6 shadow-lg"
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => setModalProduct(null)}
            className="absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:text-gray-900"
            aria-label="Cerrar"
          >
            <FiX />
          </button>
          <div className="overflow-y-auto px-2">
            <div className="mb-4 flex items-center justify-center">
              <img
                src={modalProduct.productImage || 'https://placehold.co/300'}
                alt={modalProduct.productName}
                className="h-32 w-32 rounded-lg object-cover"
              />
            </div>
            <h2 className="text-center text-xl font-bold">
              Variedades de {modalProduct.productName}
            </h2>
            {(() => {
              const visibleVarieties = filterVarietiesByGroup(
                modalProduct.Varieties || [],
                modalProduct.VarietyGroups,
                activeVarietyGroupId
              );
              return (
                <>
                  <VarietyGroupTabs
                    groups={modalProduct.VarietyGroups}
                    activeGroupId={activeVarietyGroupId}
                    onSelect={setActiveVarietyGroupId}
                    className="mt-4"
                  />
                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                    {visibleVarieties.map((variety) => {
                      const soldOut = isVarietySoldOut(variety);
                      const description = String(variety.description || '').trim();
                      return (
                        <div
                          key={variety.id}
                          className={`relative rounded-lg border bg-white p-4 shadow ${
                            soldOut ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:bg-gray-50'
                          }`}
                          onClick={() => openVariety(modalProduct, variety)}
                        >
                          {soldOut && (
                            <span className="absolute right-2 top-2 z-10 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-red-700">
                              Agotado
                            </span>
                          )}
                          <div className="mb-2 flex items-center gap-4 md:gap-8">
                            <img
                              src={variety.varietyImage || modalProduct.productImage || 'https://placehold.co/200'}
                              alt={variety.varietyName || modalProduct.productName}
                              className="h-16 w-16 rounded-lg object-cover shadow"
                            />
                            <div className="min-w-0">
                              <p className="break-words text-sm font-semibold">
                                {variety.varietyName || modalProduct.productName}
                              </p>
                              {description && (
                                <p className="mt-1 break-words text-xs text-gray-600">
                                  {description}
                                </p>
                              )}
                            </div>
                          </div>
                          {!soldOut && (
                            <div className="text-right text-xs font-semibold text-blue-600">
                              Ver formatos
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {visibleVarieties.length === 0 && (
                    <p className="mt-4 text-center text-sm text-gray-500">
                      No hay variedades para este grupo.
                    </p>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </div>
    );
  };

  const modals = (
    <>
      {showMobileCart && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 px-4 py-6"
          onClick={() => setShowMobileCart(false)}
        >
          <div
            className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between border-b border-gray-200 pb-3">
              <div>
                <h2 className="font-bold text-gray-900">{cartTitle}</h2>
                <p className="text-xs text-gray-500">{formatQuantity(cartCount)} productos seleccionados</p>
              </div>
              <button
                type="button"
                onClick={() => setShowMobileCart(false)}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:text-gray-900"
                aria-label="Cerrar"
              >
                <FiX />
              </button>
            </div>
            {renderCartPanel({ mobile: true })}
          </div>
        </div>
      )}
      {renderProductModal()}
      {modalVariety && (
        <VarietyDetailModal
          variety={modalVariety}
          onClose={() => {
            setModalVariety(null);
            setModalFormat(null);
          }}
          quantities={quantities}
          onPriceIncrement={handlePriceCardIncrement}
          onPriceDecrement={handlePriceCardDecrement}
          onPriceKeypad={(format) => setModalFormat(format)}
          onConfirmClose={() => {
            setModalVariety(null);
            setModalFormat(null);
          }}
          showPrices
          overlayZClassName={varietyModalZClassName}
        />
      )}
      {modalVariety && (
        <Modal
          isOpen={!!modalFormat}
          onClose={() => setModalFormat(null)}
          closeModalVariety={() => setModalVariety(null)}
          closeModalProduct={() => setModalProduct(null)}
          variety={modalVariety}
          edit
          quantities={quantities}
          varietyPrice={modalFormat}
          handleQuantityChange={handleQuantityChange}
          setShowDiscountModal={() => {}}
          discounts={{}}
          canDiscount={false}
          showPrices
          overlayZClassName={quantityModalZClassName}
        />
      )}
    </>
  );

  if (layout === 'modal') {
    return (
      <div className={`flex h-full min-h-0 flex-1 flex-col overflow-hidden ${className}`}>
        {topContent}
        {renderFeedbackNotice()}
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-y-auto pr-1 lg:grid-cols-[minmax(0,1fr)_380px] lg:overflow-hidden lg:pr-0">
          <div className="min-h-0 lg:overflow-y-auto lg:pr-1">
            <div className="sticky top-0 z-10 bg-white pb-3">
              <input
                type="text"
                placeholder="Buscar producto..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onFocus={(event) => event.target.select()}
                className="mb-3 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base focus:ring-2 focus:ring-blue-500 md:text-sm"
              />
              {renderCategoryPicker('')}
            </div>
            {renderProductGrid()}
          </div>
          <div className="flex min-h-[320px] flex-col lg:min-h-0">
            {renderCartPanel({ modal: true })}
          </div>
        </div>
        {modals}
      </div>
    );
  }

  const cartWrapperClassName = 'hidden md:flex md:flex-col md:justify-end fixed top-[190px] bottom-0 app-fixed-left-pane-compact px-4 z-0';
  const mainContentClassName = 'flex-1 md:pl-[max(420px,33.333333%)] p-4 overflow-auto';

  return (
    <>
      <div className={className}>
        <div className="relative flex">
          <div className={cartWrapperClassName}>
            {renderCartPanel()}
          </div>
        </div>
        <div className={mainContentClassName}>
          <div className="mx-auto max-w-7xl">
            {topContent}
            {renderFeedbackNotice()}
            <div className="mb-4 lg:hidden">
              {renderCategoryPicker()}
            </div>
            <button
              type="button"
              onClick={() => setShowMobileCart(true)}
              className="mb-4 inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 md:hidden"
            >
              <FiShoppingCart />
              Ver orden ({formatQuantity(cartCount)})
            </button>
            <div className="hidden w-full max-w-[560px] pb-4 lg:block">
              {renderCategoryPicker()}
            </div>
            <input
              type="text"
              placeholder="Buscar producto..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              onFocus={(event) => event.target.select()}
              className="mb-4 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base focus:ring-2 focus:ring-blue-500 md:text-sm"
            />
            {renderProductGrid()}
          </div>
        </div>
      </div>
      {modals}
    </>
  );
};

export default PurchaseOrderCatalogEditor;
